import React, { useContext, useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Box, MenuItem, Alert } from '@mui/material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import PhoneNumberField from '../components/PhoneNumberField';

const UserAccountPage = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    age: user?.age || '',
    sex: user?.sex || '',
    location: user?.location || '',
    user_phone: user?.user_phone || '', // Added user_phone from user data
    password: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Updated handleChange to work with both event and direct value
  const handleChange = (field) => (eventOrValue) => {
    let value;
    
    // Check if it's an event (from regular TextField) or direct value (from PhoneNumberField)
    if (typeof eventOrValue === 'object' && eventOrValue.target) {
      value = eventOrValue.target.value;
    } else {
      value = eventOrValue;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth' // Adds smooth scrolling animation
      });
  });

  const validateField = (field, value) => {
    switch (field) {
      case 'user_phone':
        return validatePhone(value);
      case 'email':
        return validateEmail(value);
      case 'newPassword':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value);
      case 'age':
        return validateAge(value);
      case 'sex':
        return validateSex(value);
      default:
        return '';
    }
  };

  const validatePhone = (phone) => {
    const regex = /^(\+20|0)?1[0-9]{9}$/;
    if (!phone) return 'رقم الهاتف مطلوب';
    if (!regex.test(phone.replace(/\s/g, ''))) {
      return 'الرجاء إدخال رقم هاتف مصري صالح';
    }
    return '';
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'الإيميل مطلوب';
    if (!regex.test(email)) return 'يرجى إدخال عنوان بريد إلكتروني صالح';
    return '';
  };

  const validatePassword = (password) => {
    if (password && password.length < 6) return 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل';
    return '';
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (confirmPassword !== formData.newPassword) return 'كلمات المرور غير متطابقة';
    return '';
  };

  const validateAge = (age) => {
    if (!age) return 'العمر مطلوب';
    if (age && (age < 1 || age > 120)) return 'الرجاء إدخال عمر صالح';
    return '';
  };

  const validateSex = (sex) => {
    if (sex === "") return 'نوع الجنس مطلوب';
    return '';
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submit
    const newTouched = {};
    const newErrors = {};
    
    Object.keys(formData).forEach(field => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).some(error => error)) {
      setMessage({ type: 'error', text: 'يرجى تصحيح الأخطاء الموجودة في النموذج' });
      return;
    }
    
    try {
      const response = await api.put('/users/update', formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      updateUser(response.data.user);
      setMessage({ type: 'success', text: 'تم تحديث الملف الشخصي بنجاح!' });
      
      // Clear password fields after successful update
      setFormData(prev => ({
        ...prev,
        password: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'فشل التحديث' });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4, mb: 10 }}>
      <Typography variant="h4" gutterBottom>
        الحساب
      </Typography>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="إسم المستخدم"
          name="username"
          value={formData.username}
          onChange={handleChange('username')}
          onBlur={handleBlur('username')}
          error={touched.username && !!errors.username}
          helperText={touched.username && errors.username}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="الإيميل"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange('email')}
          onBlur={handleBlur('email')}
          error={touched.email && !!errors.email}
          helperText={touched.email && errors.email}
          required
          disabled
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="العمر"
          type="number"
          name="age"
          value={formData.age}
          onChange={handleChange('age')}
          onBlur={handleBlur('age')}
          error={touched.age && !!errors.age}
          helperText={touched.age && errors.age}
          inputProps={{ min: 1, max: 120 }}
          required
        />
        
        <TextField
          select
          fullWidth
          margin="normal"
          label="الجنس"
          name="sex"
          value={formData.sex}
          onChange={handleChange('sex')}
          onBlur={handleBlur('sex')}
          error={touched.sex && !!errors.sex}
          helperText={touched.sex && errors.sex}
          required
        >
          <MenuItem value="">إختر نوع الجنس</MenuItem>
          <MenuItem value="male">ذكر</MenuItem>
          <MenuItem value="female">انثى</MenuItem>
        </TextField>
        
        <PhoneNumberField
          fullWidth
          margin="normal"
          label="رقم العاتف"
          name="user_phone"
          value={formData.user_phone}
          onChange={handleChange('user_phone')}
          onBlur={handleBlur('user_phone')}
          error={touched.user_phone && !!errors.user_phone}
          helperText={touched.user_phone && errors.user_phone}
          required
        />

        {!user.is_google_account && (
          <>
                  <TextField
          fullWidth
          margin="normal"
          label="كلمة المرور الحالية"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange('password')}
          onBlur={handleBlur('password')}
          error={touched.password && !!errors.password}
          helperText={touched.password && errors.password}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="كلمة المرور الجديدة"
          type="password"
          name="newPassword"
          value={formData.newPassword}
          onChange={handleChange('newPassword')}
          onBlur={handleBlur('newPassword')}
          error={touched.newPassword && !!errors.newPassword}
          helperText={touched.newPassword && errors.newPassword}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="تأكيد كلمة المرور"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange('confirmPassword')}
          onBlur={handleBlur('confirmPassword')}
          error={touched.confirmPassword && !!errors.confirmPassword}
          helperText={touched.confirmPassword && errors.confirmPassword}
        />
          </>
        )}
        
        <Button type="submit" variant="contained" sx={{ mt: 3 }} fullWidth>
          تحديث الحساب
        </Button>
      </Box>
    </Container>
  );
};

export default UserAccountPage;